import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

const formatEpoch = (ts) => {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return '';
  try {
    return new Date(n * 1000).toLocaleString();
  } catch {
    return '';
  }
};

const renderStars = (rating) => {
  const n = Number(rating);
  const r = Number.isFinite(n) ? Math.max(0, Math.min(5, Math.floor(n))) : 0;
  return '★★★★★'.slice(0, r) + '☆☆☆☆☆'.slice(0, 5 - r);
};

export default function AdminReviews() {
  const navigate = useNavigate();
  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState('');

  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const [filterStatus, setFilterStatus] = useState('PENDING_ADMIN');
  const [reviews, setReviews] = useState([]);

  const title = useMemo(() => `${PROJECT_NAME} Reviews`, []);

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const session = await Auth.currentSession();
        const groups = session.getIdToken().payload['cognito:groups'] || [];

        if (!groups.includes('Admins')) {
          navigate('/admin/login');
          return;
        }

        setUsername(user.username || user.attributes?.email || 'Admin');
        await fetchReviews(filterStatus);
      } catch {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [navigate]);

  const fetchReviews = async (status) => {
    if (!API_BASE_URL) return;
    setError('');
    setStatusMessage('');
    setBusy(true);

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const url = `${API_BASE_URL}/admin/reviews?status=${encodeURIComponent(status || 'PENDING_ADMIN')}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load reviews');
      setReviews(Array.isArray(data?.reviews) ? data.reviews : []);
    } catch (e) {
      setError(e?.message || 'Failed to load reviews');
      setReviews([]);
    } finally {
      setBusy(false);
    }
  };

  const actOnReview = async (reviewId, action) => {
    if (!API_BASE_URL) return;
    setError('');
    setStatusMessage('');
    setBusy(true);

    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      const res = await fetch(`${API_BASE_URL}/admin/reviews/${encodeURIComponent(reviewId)}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Update failed');

      setStatusMessage(`Review ${action === 'approve' ? 'approved' : 'rejected'}.`);
      await fetchReviews(filterStatus);
    } catch (e) {
      setError(e?.message || 'Update failed');
    } finally {
      setBusy(false);
      setTimeout(() => {
        try {
          statusRef.current?.focus?.();
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        } catch {
          void 0;
        }
      }, 50);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-star"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>Reviews</h1>
            <p>Approve or reject customer reviews</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield"></i>
            {username || 'Admin'}
          </span>
          <button onClick={() => Auth.signOut().then(() => navigate('/admin/login'))} className="btn-logout">
            <i className="fas fa-sign-out-alt"></i>
            Logout
          </button>
        </div>
      </header>

      <div className="admin-content">
        {error ? (
          <div className="alert alert-error" ref={statusRef} tabIndex={-1} role="alert" aria-live="assertive">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        ) : null}

        {statusMessage ? (
          <div className="alert alert-success" ref={statusRef} tabIndex={-1} role="status" aria-live="polite">
            <i className="fas fa-check-circle"></i>
            {statusMessage}
          </div>
        ) : null}

        <div className="products-list-container">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
            <label style={{ fontWeight: 800, color: '#1f1142' }}>Status</label>
            <select
              value={filterStatus}
              onChange={(e) => {
                const next = e.target.value;
                setFilterStatus(next);
                fetchReviews(next);
              }}
              disabled={busy}
              style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid rgba(148,163,184,0.6)' }}
            >
              <option value="PENDING_ADMIN">Pending approval</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="PENDING_EMAIL">Pending email verification</option>
            </select>

            <button
              type="button"
              className="btn-secondary"
              onClick={() => fetchReviews(filterStatus)}
              disabled={busy}
              style={{ marginLeft: 'auto' }}
            >
              Refresh
            </button>
          </div>

          {reviews.length === 0 ? (
            <div style={{ padding: 18, background: '#fff', borderRadius: 16, border: '1px solid rgba(148,163,184,0.35)' }}>
              No reviews.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 14 }}>
              {reviews.map((r) => {
                const id = String(r.reviewId || '');
                const status = String(r.status || '').toUpperCase();
                return (
                  <div key={id} style={{ padding: 18, background: '#fff', borderRadius: 16, border: '1px solid rgba(148,163,184,0.35)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 900, color: '#0f172a' }}>{renderStars(r.rating)} <span style={{ color: '#475569', fontWeight: 700 }}>{r.rating}/5</span></div>
                      <div style={{ color: '#475569', fontWeight: 700 }}>{status}</div>
                    </div>

                    <div style={{ marginTop: 8, fontWeight: 900, color: '#1f1142' }}>{r.title || '(No title)'}</div>
                    <div style={{ marginTop: 6, color: '#334155', whiteSpace: 'pre-wrap' }}>{r.body}</div>

                    <div style={{ marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap', color: '#475569', fontWeight: 650 }}>
                      <div>By: {r.displayName || r.submittedBy || 'Customer'}</div>
                      {r.createdAt ? <div>Submitted: {formatEpoch(r.createdAt)}</div> : null}
                      {r.approvedBy ? <div>Approved by: {r.approvedBy}</div> : null}
                    </div>

                    {status === 'PENDING_ADMIN' ? (
                      <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button type="button" className="btn-submit" onClick={() => actOnReview(id, 'approve')} disabled={busy}>Approve</button>
                        <button type="button" className="btn-secondary" onClick={() => actOnReview(id, 'reject')} disabled={busy}>Reject</button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
